/**
 * Master list of permissions. The software defines what is possible;
 * each clinic decides which roles get which permissions.
 *
 * Add new permissions here as modules are built. Never rename a key once
 * it is in use: keys are stored in the database.
 */

export interface PermissionDef {
  key: string;
  label: string;
  description: string;
  group: PermissionGroup;
  /** Hidden from the role editor until the module that uses it ships. */
  available: boolean;
  /** Only someone who has it can give it to a role or a person (Super Admin only to start). */
  restricted?: boolean;
}

export type PermissionGroup =
  | "Administration"
  | "Scheduling"
  | "Clients"
  | "Clinical"
  | "Payments"
  | "Reports";

export const PERMISSION_GROUPS: PermissionGroup[] = [
  "Administration",
  "Scheduling",
  "Clients",
  "Clinical",
  "Payments",
  "Reports",
];

const defs = [
  // Administration
  {
    key: "staff.manage",
    label: "Manage staff",
    description: "Invite, edit, and deactivate staff members, and assign their roles.",
    group: "Administration",
    available: true,
  },
  {
    key: "roles.manage",
    label: "Manage roles and permissions",
    description: "Create and edit roles, and override permissions for individual staff.",
    group: "Administration",
    available: true,
  },
  {
    key: "roles.restricted",
    label: "Give out protected permissions (Super Admin)",
    description: "Give protected permissions (like reviewing charts as the medical director, or voiding signed forms) to roles and people, or take them away, without having to hold each one.",
    group: "Administration",
    available: true,
    restricted: true,
  },
  {
    key: "staff.view_as",
    label: "View the app as another staff member",
    description: "See exactly what another staff member sees, read-only and 30 minutes at a time, to check their setup. Logged as you. Not for viewing as another Super Admin.",
    group: "Administration",
    available: true,
    restricted: true,
  },
  {
    key: "settings.manage",
    label: "Manage clinic settings",
    description: "Change clinic details, locations and hours, booking and payment rules, branding, and security settings.",
    group: "Administration",
    available: true,
  },
  {
    key: "catalog.manage",
    label: "Manage catalog",
    description: "Create, edit, and archive services, add-ons, products, and menu categories, and set prices.",
    group: "Administration",
    available: true,
  },
  {
    key: "audit.view",
    label: "View audit log",
    description: "See the record of who did what in the system.",
    group: "Administration",
    available: true,
  },

  // Scheduling
  {
    key: "availability.edit_own",
    label: "Set own availability",
    description: "Set their own weekly hours, extra shifts, and time off.",
    group: "Scheduling",
    available: true,
  },
  {
    key: "availability.view_team",
    label: "See the team's availability",
    description: "See everyone's hours and time off (read-only; time-off notes stay private).",
    group: "Scheduling",
    available: true,
  },
  {
    key: "availability.manage",
    label: "Manage everyone's availability",
    description: "See and edit every staff member's availability.",
    group: "Scheduling",
    available: true,
  },

  {
    key: "appointments.manage",
    label: "Book and manage appointments",
    description: "Book, move, check in, cancel, and mark no-shows for appointments.",
    group: "Scheduling",
    available: true,
  },
  {
    key: "today.view_all",
    label: "See everyone's visits on Today",
    description: "The Today page lists every visit at the clinic today (with \"Only mine\"), and alerts can be set to all visits. Without it, Today shows only their own upcoming visits and events.",
    group: "Scheduling",
    available: true,
  },

  // Clients (modules ship in later steps)
  {
    key: "clients.view_all",
    label: "View all clients",
    description: "See every client record in the clinic.",
    group: "Clients",
    available: true,
  },
  {
    key: "clients.view_assigned",
    label: "View assigned clients",
    description: "See only clients the staff member is booked with. Not needed alongside View all clients, which covers everyone.",
    group: "Clients",
    available: true,
  },
  {
    key: "calls.view",
    label: "Communication inbox",
    description: "The Communication page: every client's texts and emails, replies needing an answer, voicemails, and missed calls.",
    group: "Clients",
    available: true,
  },
  {
    key: "clients.edit",
    label: "Edit clients",
    description: "Add clients and update their details, addresses, tags, family links, and scheduling alert.",
    group: "Clients",
    available: true,
  },
  {
    key: "clients.merge",
    label: "Merge clients",
    description: "Combine duplicate client records into one.",
    group: "Clients",
    available: true,
  },
  {
    key: "forms.void",
    label: "Void signed forms",
    description: "Void a signed form (a test entry or the wrong client). It stays on record but no longer counts, so the client signs again if needed.",
    group: "Clinical",
    available: true,
    restricted: true,
  },
  {
    key: "clients.md_tags",
    label: "Add medical director tags",
    description: "Add or remove tags only medical directors may set, such as one that counts as a good faith exam.",
    group: "Clinical",
    available: true,
  },
  {
    key: "screenings.pause",
    label: "Pause good faith exam auto-send",
    description: "Turn off sending Qualiphy exams automatically at booking (it turns itself back on at midnight).",
    group: "Clinical",
    available: true,
  },

  // Clinical
  {
    key: "charts.write",
    label: "Chart visits",
    description: "Complete nursing charts for visits.",
    group: "Clinical",
    available: false,
  },
  {
    key: "formulary.manage",
    label: "Manage formulary and recipes",
    description: "Edit medication strengths, fluids, and supplies, and what goes into each drip, add-on, and shot. Nurses' build sheets come from these.",
    group: "Clinical",
    available: true,
  },
  {
    key: "inventory.view",
    label: "See inventory",
    description: "See medications, fluids, and supplies on hand, and record wasted or expired stock.",
    group: "Clinical",
    available: true,
  },
  {
    key: "inventory.manage",
    label: "Manage inventory",
    description: "Receive deliveries, correct counts, edit lots, and add new products while receiving.",
    group: "Clinical",
    available: true,
  },
  {
    key: "charts.view_all",
    label: "See everyone's pending charts",
    description: "On Pending Charts, see every nurse's charts still to sign and everything waiting for the medical director, not just their own.",
    group: "Clinical",
    available: true,
  },
  {
    key: "charts.sign",
    label: "Review charts (medical director)",
    description: "See charts waiting for review on Pending Charts and mark them reviewed as the medical director.",
    group: "Clinical",
    available: true,
    restricted: true,
  },

  // Payments
  {
    key: "payments.refund",
    label: "Issue refunds",
    description: "Refund payments and deposits.",
    group: "Payments",
    available: false,
  },

  {
    key: "mobile.confirm",
    label: "Confirm mobile requests",
    description: "Confirm or decline any mobile request, assign its nurse, and add an approved travel fee. Nurses can always accept requests offered to them.",
    group: "Scheduling",
    available: true,
  },

  {
    key: "events.manage",
    label: "Manage events",
    description: "Create and edit events: dates, nurses, chairs, menu, prices, and branding.",
    group: "Scheduling",
    available: true,
  },

  {
    key: "plans.sell",
    label: "Sell memberships and packages",
    description: "Sign clients up for memberships and sell packages, and take the payment.",
    group: "Payments",
    available: true,
  },
  {
    key: "plans.manage_client",
    label: "Manage client memberships",
    description: "Cancel or end a client's membership, retry a failed monthly payment, give back or add a voucher. Every change is logged.",
    group: "Payments",
    available: true,
  },

  {
    key: "prices.override",
    label: "Override prices",
    description: "Change an appointment's price when booking, with a reason that's logged.",
    group: "Payments",
    available: true,
  },

  // Reports
  {
    key: "reports.financial",
    label: "View financial reports",
    description: "See revenue and payout reports for the whole clinic.",
    group: "Reports",
    available: true,
  },
  {
    key: "payouts.view_own",
    label: "View own payouts",
    description: "See their own completed services and payouts.",
    group: "Reports",
    available: true,
  },
  {
    key: "payouts.manage",
    label: "Manage payouts",
    description: "Set pay rates, add bonuses or corrections, and mark pay periods paid.",
    group: "Reports",
    available: true,
  },
] as const satisfies readonly PermissionDef[];

export type PermissionKey = (typeof defs)[number]["key"];

export const PERMISSIONS: readonly PermissionDef[] = defs;

export const PERMISSION_KEYS = new Set<string>(defs.map((d) => d.key));

export function isPermissionKey(key: string): key is PermissionKey {
  return PERMISSION_KEYS.has(key);
}

/** Permissions only someone who has them can give out (to a role or a person). */
export const RESTRICTED_PERMISSIONS = new Set<string>(PERMISSIONS.filter((d) => d.restricted).map((d) => d.key));

/**
 * Who can give out, change, or take away a protected permission: someone with "Give out protected
 * permissions" (Super Admin), or someone who holds that permission themselves.
 */
export const canHandleRestricted = (have: ReadonlySet<string>, permission: string) =>
  !RESTRICTED_PERMISSIONS.has(permission) || have.has(permission) || have.has("roles.restricted");

/**
 * Effective permissions = union of role permissions,
 * plus per-person grants, minus per-person revokes.
 */
export function effectivePermissions(
  rolePermissions: Iterable<string>,
  overrides: Iterable<{ permission: string; effect: "grant" | "revoke" }>,
): Set<PermissionKey> {
  const result = new Set<string>();
  for (const p of rolePermissions) if (isPermissionKey(p)) result.add(p);
  const list = [...overrides];
  for (const o of list) if (o.effect === "grant" && isPermissionKey(o.permission)) result.add(o.permission);
  for (const o of list) if (o.effect === "revoke") result.delete(o.permission);
  return result as Set<PermissionKey>;
}

/** Starting roles for a new clinic. Clinics can edit these freely. */
export const DEFAULT_ROLES: { name: string; description: string; permissions: PermissionKey[] }[] = [
  {
    name: "Super Admin",
    description: "Everything Admin has, plus restricted actions such as voiding signed forms. Only a Super Admin can give these out.",
    permissions: defs.map((d) => d.key),
  },
  {
    name: "Admin",
    description: "Full access, including settings, staff, roles, and financials.",
    permissions: defs.filter((d) => !("restricted" in d && d.restricted)).map((d) => d.key),
  },
  {
    name: "RN Manager",
    description: "Runs scheduling, clients, charting, and day-to-day operations.",
    permissions: [
      "staff.manage",
      "catalog.manage",
      "formulary.manage",
      "inventory.view",
      "inventory.manage",
      "availability.edit_own",
      "availability.view_team",
      "availability.manage",
      "appointments.manage",
      "today.view_all",
      "mobile.confirm",
      "events.manage",
      "prices.override",
      "plans.sell",
      "plans.manage_client",
      "audit.view",
      "clients.view_all",
      "clients.edit",
      "clients.merge",
      "screenings.pause",
      "calls.view",
      "charts.write",
      "charts.view_all",
      "payments.refund",
      "reports.financial",
      "payouts.view_own",
    ],
  },
  {
    name: "RN",
    description: "Own schedule, assigned clients, charting, and own payouts.",
    permissions: ["clients.view_assigned", "clients.edit", "charts.write", "inventory.view", "payouts.view_own", "availability.edit_own", "availability.view_team", "appointments.manage", "plans.sell"],
  },
  {
    name: "Medical Director",
    description: "Reads client records and charts; reviews and signs charts.",
    permissions: ["clients.view_all", "charts.sign", "formulary.manage", "inventory.view", "inventory.manage", "clients.md_tags"],
  },
];
